import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  Chip,
  TextField
} from '@mui/material';
import {
  Download as DownloadIcon,
  Description as DescriptionIcon,
  DirectionsCar as CarIcon,
  Person as PersonIcon,
  AttachFile as DocumentIcon,
  TripOrigin as TripIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import moment from 'moment/min/moment-with-locales';
import { MAIN_COLOR, SECONDORY_COLOR, downloadCsv } from '../common/sharedFunctions';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

export default function AdminReports() {
  const auth = useSelector(state => state.auth);
  const usersdata = useSelector(state => state.usersdata);
  const carlistdata = useSelector(state => state.carlistdata);
  const bookingdata = useSelector(state => state.bookinglistdata);
  const settings = useSelector(state => state.settingsdata.settings);
  const [loading, setLoading] = useState(false);
  const [reportType, setReportType] = useState('');
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [availableCountries, setAvailableCountries] = useState([]);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Extract unique countries from users data
  useEffect(() => {
    if (usersdata.users) {
      const countries = [...new Set(usersdata.users.map(user => user.country).filter(Boolean))];
      setAvailableCountries(countries.sort());
    }
  }, [usersdata.users]);

  const reportTypes = [
    {
      id: 'drivers',
      title: 'Drivers Report',
      description: 'Complete list of all registered drivers with their details',
      icon: <PersonIcon fontSize="large" />,
      color: '#1976d2'
    },
    {
      id: 'cars',
      title: 'Cars Report',
      description: 'All registered vehicles with driver assignments',
      icon: <CarIcon fontSize="large" />,
      color: '#2e7d32'
    },
    {
      id: 'documents',
      title: 'Documents Report',
      description: 'Driver documents status and verification details',
      icon: <DocumentIcon fontSize="large" />,
      color: '#ed6c02'
    },
    {
      id: 'trips',
      title: 'Trips Report',
      description: 'Complete trip history with earnings and details',
      icon: <TripIcon fontSize="large" />,
      color: '#9c27b0'
    },
    {
      id: 'riders',
      title: 'Riders Report',
      description: 'All registered customers/riders',
      icon: <PersonIcon fontSize="large" />,
      color: '#0288d1'
    },
    {
      id: 'summary',
      title: 'Summary Report',
      description: 'Overall statistics and performance metrics',
      icon: <AssessmentIcon fontSize="large" />,
      color: '#d32f2f'
    }
  ];

  const generateDriversReport = () => {
    if (!usersdata.users) return null;

    let drivers = usersdata.users.filter(user => user.usertype === 'driver');
    
    // Filter by country if not 'all'
    if (selectedCountry !== 'all') {
      drivers = drivers.filter(driver => driver.country === selectedCountry);
    }
    
    let csvContent = 'Driver ID,First Name,Last Name,Email,Mobile,Country,Approved,Active Status,License Number,Rating,Queue,Wallet Balance,Created Date,Last Location\n';
    
    drivers.forEach(driver => {
      const row = [
        driver.id || '',
        driver.firstName || '',
        driver.lastName || '',
        settings.AllowCriticalEditsAdmin ? (driver.email || '') : 'hidden',
        settings.AllowCriticalEditsAdmin ? (driver.mobile || '') : 'hidden',
        driver.country || '',
        driver.approved ? 'Yes' : 'No',
        driver.driverActiveStatus ? 'Active' : 'Inactive',
        driver.licenseNumber || '',
        driver.rating || '0',
        driver.queue ? 'Yes' : 'No',
        driver.walletBalance || '0',
        driver.createdAt ? moment(driver.createdAt).format('YYYY-MM-DD HH:mm:ss') : '',
        driver.location ? `${driver.location.lat},${driver.location.lng}` : ''
      ].map(field => `"${String(field).replace(/"/g, '""')}"`).join(',');
      
      csvContent += row + '\n';
    });

    return {
      content: csvContent,
      filename: `drivers_report_${selectedCountry !== 'all' ? selectedCountry + '_' : ''}${moment().format('YYYY-MM-DD')}.csv`,
      count: drivers.length
    };
  };

  const generateCarsReport = () => {
    if (!carlistdata.cars) return null;

    let cars = carlistdata.cars;
    
    // Filter by country if not 'all' - filter by driver's country
    if (selectedCountry !== 'all' && usersdata.users) {
      cars = cars.filter(car => {
        if (car.driver) {
          const driver = usersdata.users.find(u => u.id === car.driver);
          return driver && driver.country === selectedCountry;
        }
        return false;
      });
    }

    let csvContent = 'Car ID,Car Type,Car Number,Brand,Model,Make Year,Other Info,Driver Name,Driver ID,Active,Approved,Created Date\n';
    
    cars.forEach(car => {
      // Get driver name
      let driverName = '';
      if (car.driver && usersdata.users) {
        const driver = usersdata.users.find(u => u.id === car.driver);
        if (driver) {
          driverName = `${driver.firstName} ${driver.lastName}`;
        }
      }

      const row = [
        car.id || '',
        car.carType || '',
        car.vehicleNumber || '',
        car.vehicleMake || '',
        car.vehicleModel || '',
        car.year || '',
        car.other_info || '',
        driverName,
        car.driver || '',
        car.active ? 'Yes' : 'No',
        car.approved ? 'Yes' : 'No',
        car.createdAt ? moment(car.createdAt).format('YYYY-MM-DD HH:mm:ss') : ''
      ].map(field => `"${String(field).replace(/"/g, '""')}"`).join(',');
      
      csvContent += row + '\n';
    });

    return {
      content: csvContent,
      filename: `cars_report_${selectedCountry !== 'all' ? selectedCountry + '_' : ''}${moment().format('YYYY-MM-DD')}.csv`,
      count: cars.length
    };
  };

  const generateDocumentsReport = () => {
    if (!usersdata.users) return null;

    let drivers = usersdata.users.filter(user => user.usertype === 'driver');
    
    // Filter by country if not 'all'
    if (selectedCountry !== 'all') {
      drivers = drivers.filter(driver => driver.country === selectedCountry);
    }
    
    let csvContent = 'Driver ID,Driver Name,License Image,License Image Valid,Vehicle Registration,Vehicle Registration Valid,Insurance Image,Insurance Image Valid,Verified Date\n';
    
    drivers.forEach(driver => {
      const row = [
        driver.id || '',
        `${driver.firstName} ${driver.lastName}`,
        driver.licenseImage ? 'Uploaded' : 'Missing',
        driver.licenseImageValid ? 'Valid' : 'Invalid',
        driver.vehicleRegistrationImage ? 'Uploaded' : 'Missing',
        driver.vehicleRegistrationImageValid ? 'Valid' : 'Invalid',
        driver.insuranceImage ? 'Uploaded' : 'Missing',
        driver.insuranceImageValid ? 'Valid' : 'Invalid',
        driver.verifyDate ? moment(driver.verifyDate).format('YYYY-MM-DD HH:mm:ss') : ''
      ].map(field => `"${String(field).replace(/"/g, '""')}"`).join(',');
      
      csvContent += row + '\n';
    });

    return {
      content: csvContent,
      filename: `documents_report_${selectedCountry !== 'all' ? selectedCountry + '_' : ''}${moment().format('YYYY-MM-DD')}.csv`,
      count: drivers.length
    };
  };

  const generateTripsReport = () => {
    if (!bookingdata.bookings) return null;

    let trips = bookingdata.bookings;
    
    // Filter by date range if set
    if (dateRange.start && dateRange.end) {
      trips = trips.filter(trip => {
        const tripDate = moment(trip.tripdate);
        return tripDate.isBetween(dayjs(dateRange.start).startOf('day'), dayjs(dateRange.end).endOf('day'), null, '[]');
      });
    }

    // Filter by country if not 'all' - use booking's country field (set when booking is created)
    if (selectedCountry !== 'all') {
      trips = trips.filter(trip => trip.country === selectedCountry);
    }

    let csvContent = 'Booking ID,Trip Date,Status,Customer Name,Driver Name,Pickup,Dropoff,Car Type,Distance,Duration,Trip Cost,Driver Share,Commission,Convenience Fee,Discount,Payment Mode,Payment Status,Created Date\n';
    
    trips.forEach(trip => {
      // Get customer name
      let customerName = '';
      if (trip.customer && usersdata.users) {
        const customer = usersdata.users.find(u => u.id === trip.customer);
        if (customer) {
          customerName = `${customer.firstName} ${customer.lastName}`;
        }
      }

      // Get driver name
      let driverName = '';
      if (trip.driver && usersdata.users) {
        const driver = usersdata.users.find(u => u.id === trip.driver);
        if (driver) {
          driverName = `${driver.firstName} ${driver.lastName}`;
        }
      }

      const row = [
        trip.id || '',
        trip.tripdate ? moment(trip.tripdate).format('YYYY-MM-DD HH:mm:ss') : '',
        trip.status || '',
        customerName,
        driverName,
        trip.pickupAddress || '',
        trip.dropAddress || '',
        trip.carType || '',
        trip.distance ? `${trip.distance} km` : '',
        trip.estimateTime ? `${trip.estimateTime} min` : '',
        trip.trip_cost || '0',
        trip.driver_share || '0',
        trip.commission_amount || '0',
        trip.convenience_fees || '0',
        trip.discount || '0',
        trip.payment_mode || '',
        trip.status === 'PAID' ? 'Paid' : 'Pending',
        trip.createdAt ? moment(trip.createdAt).format('YYYY-MM-DD HH:mm:ss') : ''
      ].map(field => `"${String(field).replace(/"/g, '""')}"`).join(',');
      
      csvContent += row + '\n';
    });

    return {
      content: csvContent,
      filename: `trips_report_${selectedCountry !== 'all' ? selectedCountry + '_' : ''}${moment().format('YYYY-MM-DD')}.csv`,
      count: trips.length
    };
  };

  const generateRidersReport = () => {
    if (!usersdata.users) return null;

    let riders = usersdata.users.filter(user => user.usertype === 'rider' || user.usertype === 'customer');
    
    // Filter by country if not 'all'
    if (selectedCountry !== 'all') {
      riders = riders.filter(rider => rider.country === selectedCountry);
    }
    
    let csvContent = 'Rider ID,First Name,Last Name,Email,Mobile,Country,Referral Code,Wallet Balance,Signup Via Referral,Created Date\n';
    
    riders.forEach(rider => {
      const row = [
        rider.id || '',
        rider.firstName || '',
        rider.lastName || '',
        settings.AllowCriticalEditsAdmin ? (rider.email || '') : 'hidden',
        settings.AllowCriticalEditsAdmin ? (rider.mobile || '') : 'hidden',
        rider.country || '',
        rider.referralId || '',
        rider.walletBalance || '0',
        rider.signupViaReferral || '',
        rider.createdAt ? moment(rider.createdAt).format('YYYY-MM-DD HH:mm:ss') : ''
      ].map(field => `"${String(field).replace(/"/g, '""')}"`).join(',');
      
      csvContent += row + '\n';
    });

    return {
      content: csvContent,
      filename: `riders_report_${selectedCountry !== 'all' ? selectedCountry + '_' : ''}${moment().format('YYYY-MM-DD')}.csv`,
      count: riders.length
    };
  };

  const generateSummaryReport = () => {
    if (!usersdata.users || !bookingdata.bookings || !carlistdata.cars) return null;

    let drivers = usersdata.users.filter(u => u.usertype === 'driver');
    let riders = usersdata.users.filter(u => u.usertype === 'rider' || u.usertype === 'customer');
    
    // Filter by country if not 'all'
    if (selectedCountry !== 'all') {
      drivers = drivers.filter(d => d.country === selectedCountry);
      riders = riders.filter(r => r.country === selectedCountry);
    }
    
    const activeDrivers = drivers.filter(d => d.driverActiveStatus);
    const approvedDrivers = drivers.filter(d => d.approved);
    
    // Filter cars by country if not 'all'
    let carsFiltered = carlistdata.cars;
    if (selectedCountry !== 'all' && usersdata.users) {
      carsFiltered = carlistdata.cars.filter(car => {
        if (car.driver) {
          const driver = usersdata.users.find(u => u.id === car.driver);
          return driver && driver.country === selectedCountry;
        }
        return false;
      });
    }
    
    const activeCars = carsFiltered.filter(c => c.active);
    const approvedCars = carsFiltered.filter(c => c.approved);
    
    let trips = bookingdata.bookings;
    
    // Filter by date range if set
    if (dateRange.start && dateRange.end) {
      trips = trips.filter(trip => {
        const tripDate = moment(trip.tripdate);
        return tripDate.isBetween(dayjs(dateRange.start).startOf('day'), dayjs(dateRange.end).endOf('day'), null, '[]');
      });
    }

    // Filter by country if not 'all' - use booking's country field (set when booking is created)
    if (selectedCountry !== 'all') {
      trips = trips.filter(trip => trip.country === selectedCountry);
    }

    const completedTrips = trips.filter(t => t.status === 'COMPLETE');
    const totalRevenue = completedTrips.reduce((sum, t) => sum + parseFloat(t.trip_cost || 0), 0);
    const totalCommission = completedTrips.reduce((sum, t) => sum + parseFloat(t.commission_amount || 0), 0);
    const totalDriverShare = completedTrips.reduce((sum, t) => sum + parseFloat(t.driver_share || 0), 0);

    let csvContent = 'Metric,Value\n';
    csvContent += `Country,${selectedCountry === 'all' ? 'All Countries' : selectedCountry}\n`;
    csvContent += `Total Drivers,${drivers.length}\n`;
    csvContent += `Active Drivers,${activeDrivers.length}\n`;
    csvContent += `Approved Drivers,${approvedDrivers.length}\n`;
    csvContent += `Total Riders,${riders.length}\n`;
    csvContent += `Total Cars,${carsFiltered.length}\n`;
    csvContent += `Active Cars,${activeCars.length}\n`;
    csvContent += `Approved Cars,${approvedCars.length}\n`;
    csvContent += `Total Trips${dateRange.start ? ' (Filtered)' : ''},${trips.length}\n`;
    csvContent += `Completed Trips,${completedTrips.length}\n`;
    csvContent += `Total Revenue,${settings?.symbol || '$'}${totalRevenue.toFixed(2)}\n`;
    csvContent += `Total Commission,${settings?.symbol || '$'}${totalCommission.toFixed(2)}\n`;
    csvContent += `Total Driver Share,${settings?.symbol || '$'}${totalDriverShare.toFixed(2)}\n`;
    csvContent += `Report Generated,${moment().format('YYYY-MM-DD HH:mm:ss')}\n`;

    return {
      content: csvContent,
      filename: `summary_report_${selectedCountry !== 'all' ? selectedCountry + '_' : ''}${moment().format('YYYY-MM-DD')}.csv`,
      count: 1
    };
  };

  const handleDownloadReport = async () => {
    if (!reportType) {
      setError('Please select a report type');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      let reportData = null;

      switch (reportType) {
        case 'drivers':
          reportData = generateDriversReport();
          break;
        case 'cars':
          reportData = generateCarsReport();
          break;
        case 'documents':
          reportData = generateDocumentsReport();
          break;
        case 'trips':
          reportData = generateTripsReport();
          break;
        case 'riders':
          reportData = generateRidersReport();
          break;
        case 'summary':
          reportData = generateSummaryReport();
          break;
        default:
          throw new Error('Invalid report type');
      }

      if (reportData) {
        downloadCsv(reportData.content, reportData.filename);
        setSuccess(`Report downloaded successfully! ${reportData.count} records`);
      } else {
        setError('No data available for this report');
      }
    } catch (err) {
      console.error('Error generating report:', err);
      setError('Error generating report');
    } finally {
      setLoading(false);
    }
  };

  const getReportStats = () => {
    if (!usersdata.users || !bookingdata.bookings || !carlistdata.cars) {
      return null;
    }

    const drivers = usersdata.users.filter(u => u.usertype === 'driver');
    const riders = usersdata.users.filter(u => u.usertype === 'rider' || u.usertype === 'customer');
    
    return {
      drivers: drivers.length,
      riders: riders.length,
      cars: carlistdata.cars.length,
      trips: bookingdata.bookings.length
    };
  };

  const stats = getReportStats();

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ width: '100%', p: 3 }}>
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <DescriptionIcon sx={{ fontSize: 40, color: MAIN_COLOR, mr: 2 }} />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: MAIN_COLOR }}>
                Admin Reports
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Download comprehensive reports for all system data
              </Typography>
            </Box>
          </Box>

          {stats && (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
              <Chip label={`${stats.drivers} Drivers`} color="primary" />
              <Chip label={`${stats.riders} Riders`} color="info" />
              <Chip label={`${stats.cars} Cars`} color="success" />
              <Chip label={`${stats.trips} Trips`} color="secondary" />
            </Box>
          )}
        </Paper>

        {/* Country Filter */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Select Country
          </Typography>
          <FormControl fullWidth>
            <InputLabel id="country-select-label">Country</InputLabel>
            <Select
              labelId="country-select-label"
              id="country-select"
              value={selectedCountry}
              label="Country"
              onChange={(e) => setSelectedCountry(e.target.value)}
            >
              <MenuItem value="all">All Countries</MenuItem>
              {availableCountries.map(country => (
                <MenuItem key={country} value={country}>
                  {country}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedCountry !== 'all' && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              Reports will be filtered for: <strong>{selectedCountry}</strong>
            </Typography>
          )}
        </Paper>

        {/* Report Type Selection */}
        <Grid container spacing={3}>
          {reportTypes.map(report => (
            <Grid item xs={12} sm={6} md={4} key={report.id}>
              <Card 
                sx={{ 
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  border: reportType === report.id ? `2px solid ${report.color}` : '1px solid #e0e0e0',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
                onClick={() => setReportType(report.id)}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ color: report.color, mr: 2 }}>
                      {report.icon}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {report.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    {report.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Date Range Filter for Trips */}
        {reportType === 'trips' || reportType === 'summary' ? (
          <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Date Range Filter (Optional)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Start Date"
                  value={dateRange.start}
                  onChange={(newValue) => setDateRange({ ...dateRange, start: newValue })}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="End Date"
                  value={dateRange.end}
                  onChange={(newValue) => setDateRange({ ...dateRange, end: newValue })}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
            </Grid>
            {(dateRange.start || dateRange.end) && (
              <Button
                size="small"
                onClick={() => setDateRange({ start: null, end: null })}
                sx={{ mt: 1 }}
              >
                Clear Filter
              </Button>
            )}
          </Paper>
        ) : null}

        {/* Download Button */}
        <Paper elevation={2} sx={{ p: 3, mt: 3, textAlign: 'center' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
            onClick={handleDownloadReport}
            disabled={!reportType || loading}
            sx={{
              backgroundColor: MAIN_COLOR,
              '&:hover': { backgroundColor: SECONDORY_COLOR },
              px: 4,
              py: 1.5
            }}
          >
            {loading ? 'Generating...' : 'Download Report'}
          </Button>

          {success && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {success}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Paper>

        {/* Info Section */}
        <Paper elevation={1} sx={{ p: 2, mt: 3, backgroundColor: '#f5f5f5' }}>
          <Typography variant="body2" color="textSecondary">
            <strong>Note:</strong> All reports are generated in CSV format and can be opened in Excel or Google Sheets. Date filters apply only to trip-related reports.
          </Typography>
        </Paper>
      </Box>
    </LocalizationProvider>
  );
}
